import React from "react";
import styled from "styled-components";
import {
  ResponsiveScatterPlot,
  ScatterPlotTooltipProps,
} from "@nivo/scatterplot";

import { ChartEmptyState } from "./ChartEmptyState";
import {
  Card,
  ElementContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import { LegendCircle } from "../../components/visuals/styledComponents";
import { darkGreen, graphPaletteV2, greyText, lightGreys } from "../../styles";
import {
  PillarsSinsScatterData,
  ScatterPoint,
} from "../../types/engagementAssessmentV2Types";

const SERIES_ID = "Engagements";

const POINT_COLOR = graphPaletteV2[0];

const MAX_SIN_COUNT = 7;

const ChartContainer = styled.div`
  height: 340px;
  width: 100%;
`;

const TooltipBox = styled.div`
  background: white;
  padding: 10px 14px;
  border: 1px solid ${lightGreys.borderLightGrey};
  border-radius: 7px;
  font-family: "Apercu";
  font-size: 12px;
  min-width: 180px;
`;

const ScatterTooltip = ({
  node,
}: ScatterPlotTooltipProps<ScatterPoint>): JSX.Element => {
  const data = node.data as ScatterPoint;
  return (
    <TooltipBox>
      <CompassParagraph bold fontSize={13} mb={4}>
        {data.unitLabel}
      </CompassParagraph>
      <ElementContainer w="100%" jc="space-between" ai="center" mt={2}>
        <CompassParagraph color={greyText} fontSize={11}>
          Mean pillar score:
        </CompassParagraph>
        <CompassParagraph fontSize={11}>{data.x.toFixed(2)}</CompassParagraph>
      </ElementContainer>
      <ElementContainer w="100%" jc="space-between" ai="center" mt={2}>
        <CompassParagraph color={greyText} fontSize={11}>
          Present sins:
        </CompassParagraph>
        <CompassParagraph fontSize={11}>{data.y}</CompassParagraph>
      </ElementContainer>
      <ElementContainer w="100%" jc="space-between" ai="center" mt={2}>
        <CompassParagraph color={greyText} fontSize={11}>
          Engagements:
        </CompassParagraph>
        <CompassParagraph fontSize={11}>{data.count}</CompassParagraph>
      </ElementContainer>
    </TooltipBox>
  );
};

const scatterTheme = {
  text: { fontFamily: "Apercu", fontSize: 11, fill: darkGreen },
  axis: {
    ticks: {
      text: { fontFamily: "Apercu", fontSize: 11, fill: greyText },
    },
    legend: {
      text: { fontFamily: "Apercu", fontSize: 12, fill: darkGreen },
    },
  },
  grid: {
    line: {
      stroke: lightGreys.borderLightGrey,
      strokeDasharray: "4 4",
    },
  },
};

export const PillarsSinsScatter = ({
  data,
  isLoading,
  error,
  onRetry,
}: PillarsSinsScatterProps): JSX.Element => {
  const isEmpty = data.points.length === 0;

  const series = [
    {
      id: SERIES_ID,
      data: data.points,
    },
  ];

  return (
    <Card h="500px" isLoading={isLoading}>
      <RowContainer mt={0} mb={8}>
        <CompassParagraph fontSize={16} bold>
          Pillars vs Sins
        </CompassParagraph>
      </RowContainer>
      <CompassParagraph fontSize={12} color={greyText} mb={8}>
        Each point is one engagement. The bottom-right quadrant — high mean
        pillar score with few present sins — is the ideal.
      </CompassParagraph>

      {error && !isLoading ? (
        <ChartEmptyState
          variant="error"
          entityName="engagements"
          onRetry={onRetry}
        />
      ) : isEmpty && !isLoading ? (
        <ChartEmptyState variant="empty" entityName="engagements" />
      ) : (
        <>
          <ChartContainer>
            <ResponsiveScatterPlot<ScatterPoint>
              data={series}
              margin={{ top: 20, right: 24, bottom: 60, left: 70 }}
              xScale={{ type: "linear", min: 0, max: 1 }}
              yScale={{ type: "linear", min: 0, max: MAX_SIN_COUNT }}
              colors={POINT_COLOR}
              nodeSize={10}
              useMesh
              tooltip={ScatterTooltip}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                tickValues: 5,
                format: (v): string => Number(v).toFixed(2),
                legend: "Mean pillar score →",
                legendPosition: "middle",
                legendOffset: 46,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                tickValues: MAX_SIN_COUNT + 1,
                format: (v): string => String(Math.round(Number(v))),
                legend: "Present sins ↑",
                legendPosition: "middle",
                legendOffset: -50,
              }}
              gridXValues={[0.13, 0.38, 0.63, 0.88]}
              gridYValues={[0, 1, 2, 3, 4, 5, 6, 7]}
              theme={scatterTheme}
              animate
              motionConfig="gentle"
            />
          </ChartContainer>

          <ElementContainer w="100%" ai="center" jc="flex-start" mt={8}>
            <ElementContainer ai="center" mr={12}>
              <LegendCircle color={POINT_COLOR} size={10} />
              <CompassParagraph color={darkGreen} fontSize={12} mr={0}>
                Engagement
              </CompassParagraph>
            </ElementContainer>
            <CompassParagraph color={greyText} fontSize={11} mr={0}>
              Ideal quadrant: bottom-right (high mean pillar score, few sins).
            </CompassParagraph>
          </ElementContainer>
        </>
      )}
    </Card>
  );
};

type PillarsSinsScatterProps = {
  data: PillarsSinsScatterData;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
};
